import React from 'react';
import { DroneEntity, DroneStatusType } from '@src/core/features';
import {
  findDroneBySerial,
  isSerialInMissionBucket,
} from '@src/features/drone/utils/find-drone-by-serial';

function mergePreservedFields(
  base: DroneEntity,
  preserved: Pick<DroneEntity, 'currentOrderId' | 'missionId'>,
): DroneEntity {
  return {
    ...base,
    currentOrderId: base.currentOrderId ?? preserved.currentOrderId,
    missionId: base.missionId ?? preserved.missionId,
  };
}

export const useDroneSelection = (
  selected: DroneEntity | null,
  flyingDrones: DroneEntity[],
  missionDrones: DroneEntity[],
  idleDrones: DroneEntity[] | undefined,
  allDrones: DroneEntity[],
  optimisticMissionSerials?: string[],
) => {
  const selectedDrone = React.useMemo(() => {
    if (!selected) return null;

    const serial = selected.serialNumber;
    const idleDrone = idleDrones?.find((d) => d.serialNumber === serial);
    const flyingDrone = flyingDrones.find((d) => d.serialNumber === serial);
    const missionDrone = missionDrones.find((d) => d.serialNumber === serial);
    const bestBySerial = findDroneBySerial(serial, missionDrones, idleDrones ?? [], allDrones);
    const preserved = {
      currentOrderId: selected.currentOrderId,
      missionId: selected.missionId,
    };

    const isOptimisticTakeoff = optimisticMissionSerials?.includes(serial) ?? false;

    if (selected.status === DroneStatusType.IDLE && !isOptimisticTakeoff) {
      const activeMissionRow =
        (missionDrone && missionDrone.status !== DroneStatusType.IDLE
          ? missionDrone
          : undefined) ??
        (flyingDrone && flyingDrone.status !== DroneStatusType.IDLE ? flyingDrone : undefined) ??
        (bestBySerial && bestBySerial.status !== DroneStatusType.IDLE ? bestBySerial : undefined);

      if (activeMissionRow) {
        return mergePreservedFields(activeMissionRow, preserved);
      }

      return idleDrone ? mergePreservedFields(idleDrone, preserved) : selected;
    }

    if (flyingDrone) return mergePreservedFields(flyingDrone, preserved);
    if (idleDrone && missionDrone && bestBySerial) {
      return mergePreservedFields(bestBySerial, preserved);
    }
    if (missionDrone) return mergePreservedFields(missionDrone, preserved);
    if (idleDrone) return mergePreservedFields(idleDrone, preserved);

    const fromAll = allDrones.find((d) => d.serialNumber === serial);
    if (fromAll) return mergePreservedFields(fromAll, preserved);
    if (bestBySerial) return mergePreservedFields(bestBySerial, preserved);

    return selected;
  }, [flyingDrones, missionDrones, idleDrones, allDrones, selected, optimisticMissionSerials]);

  const isOptimisticallyOnMission = Boolean(
    selectedDrone && optimisticMissionSerials?.includes(selectedDrone.serialNumber),
  );

  const isStrictMission = selectedDrone?.status === DroneStatusType.MISSION;

  const hasActiveOrder =
    selectedDrone?.currentOrderId != null && Number(selectedDrone.currentOrderId) !== 0;

  const isOnMission = Boolean(
    selectedDrone &&
      (isOptimisticallyOnMission ||
        isSerialInMissionBucket(selectedDrone.serialNumber, missionDrones) ||
        selectedDrone.status !== DroneStatusType.IDLE ||
        hasActiveOrder),
  );

  return { selectedDrone, isOnMission, isStrictMission, isOptimisticallyOnMission };
};
